import styled from 'styled-components';
import { Link } from 'react-router-dom';

import Input from '~/components/Form/Input';

import colors from '~/styles/colors';
import { device } from '~/styles/device';

export const Container = styled.div`
  margin: 20px auto;
  max-width: 1180px;
  padding: 0 16px;
  width: 100%;

  @media ${device.laptop} {
    padding: 0 30px;
  }

  .title-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 24px;

    p {
      margin-top: 20px;
      line-height: 24px;
    }

    a {
      text-decoration: none;
      color: ${colors.danger};
      font-weight: bold;

      &:hover {
        filter: brightness(90%);
      }
    }
  }
`;

export const Header = styled.header`
  align-items: center;
  display: flex;
  flex-direction: column;

  @media ${device.laptop} {
    align-items: flex-end;
    flex-direction: row;
    justify-content: space-between;
  }

  > div {
    align-items: center;
    display: flex;
    width: 100%;

    @media ${device.mobileL} {
      width: auto;
    }

    &:first-child {
      flex-direction: column;
      margin-bottom: 20px;

      @media ${device.laptop} {
        align-items: flex-end;
        flex-direction: row;
        margin-bottom: 0;
      }
    }
  }

  span {
    font-size: 20px;
    margin-left: 0;
    margin-top: 15px;

    @media ${device.laptop} {
      margin-left: 24px;
      margin-top: 0;
    }

    > span {
      font-weight: 500;
      margin: 10px 0 0;
    }
  }

  svg {
    height: 64px;
  }

  img {
    height: 104px;
  }
`;

export const RegisterLink = styled(Link)`
  align-items: center;
  background-color: ${colors.danger};
  border-radius: 8px;
  color: ${colors.white};
  display: flex;
  font-size: 17px;
  font-weight: 700;
  height: 60px;
  justify-content: center;
  margin-left: auto;
  text-align: center;
  text-decoration: none;
  transition: all 300ms ease;
  width: 100%;

  @media ${device.mobileL} {
    font-size: 18px;
    width: 260px;
  }

  &:hover {
    filter: brightness(90%);
  }
`;

export const LogoutButton = styled.button.attrs({
  type: 'button',
})`
  background-color: transparent;
  border: solid 1px ${colors.lightGrey};
  border-radius: 4px;
  color: ${colors.white};
  display: flex;
  font-size: 18px;
  font-weight: 700;
  height: 60px;
  margin-left: 16px;
  width: 60px;
  transition: all 300ms ease;
  align-items: center;
  justify-content: center;

  svg {
    height: 18px;
  }

  &:hover {
    filter: brightness(90%);
  }
`;

export const Content = styled.div`
  h1 {
    align-items: center;
    display: flex;
    font-size: 1.5rem;
    flex-direction: column;
    margin-top: 40px;
    width: 100%;

    @media ${device.laptop} {
      flex-direction: row;
      font-size: 2rem;
      margin-top: 80px;
    }

    span {
      color: ${colors.danger};
      font-size: 16px;
      font-weight: 600;
      margin-top: 10px;

      @media ${device.laptop} {
        margin-left: 10px;
        margin-top: 0;
      }
    }
  }

  ul {
    display: grid;
    grid-gap: 24px;
    grid-template-columns: 1fr;

    @media ${device.laptop} {
      grid-template-columns: repeat(2, 1fr);
    }

    li {
      background-color: ${colors.white};
      padding: 24px;
      border-radius: 8px;
      position: relative;
      display: flex;
      flex-direction: column;

      strong {
        color: ${colors.darkGrey2};
        display: block;
        margin-bottom: 5px;
        font-size: 12px;
      }

      p {
        color: ${colors.grey2};
        line-height: 20px;
        font-size: 16px;

        + strong {
          margin-top: 22px;
        }
      }

      .info-group {
        display: grid;
        flex: 1;
        grid-template-columns: 1fr;

        @media ${device.laptop} {
          grid-template-columns: repeat(2, 1fr);
        }

        > div {
          margin-bottom: 20px;
        }
      }

      .button-group {
        margin-top: 0px;

        > div {
          display: flex;
          justify-content: flex-start;
          flex-direction: column;

          @media ${device.laptop} {
            flex-direction: row;
            justify-content: space-between;
          }
        }

        strong {
          margin-bottom: 20px;
        }

        a,
        button {
          background-color: ${colors.danger};
          color: ${colors.white};
          border-radius: 4px;
          border: 0;
          display: flex;
          font-size: 14px;
          font-weight: 700;
          height: 45px;
          justify-content: center;
          text-align: center;
          text-decoration: none;
          transition: all 300ms ease;
          align-items: center;
          padding: 0 25px;

          + a,
          button {
            margin-top: 10px;

            @media ${device.laptop} {
              margin-top: 0;
            }
          }

          svg {
            margin-right: 10px;
          }
        }
      }
    }
  }
`;

export const RemoveButton = styled.button.attrs({
  type: 'button',
})`
  background-color: transparent;
  border: 0;
  position: absolute;
  right: 24px;
  top: 24px;
  transition: opacity 300ms ease;

  &:hover {
    opacity: 0.8;
  }
`;

export const Pagination = styled.div`
  margin-top: 50px;
  display: flex;
  justify-content: space-between;

  button {
    background-color: transparent;
    border: solid 1px ${colors.grey3};
    color: ${colors.grey3};
    padding: 10px;
    border-radius: 4px;
    font-weight: bold;

    &[disabled] {
      border-color: ${colors.lightGrey};
      color: ${colors.lightGrey};
    }
  }
`;

export const SearchBox = styled.div`
  margin-bottom: 30px;
  position: relative;

  svg {
    bottom: 0;
    left: 20px;
    position: absolute;
    top: 25px;
    z-index: 1;
  }

  input {
    border: 0;
    padding-left: 70px;
  }
`;

export const InputWrapper = styled(Input).attrs(({ type }) => ({
  type: type || 'text',
}))`
  border-radius: 8px;
  border: solid 1px ${colors.lightGrey};
  color: ${colors.darkGrey};
  height: 60px;
  margin-top: 8px;
  padding: 0 24px;
  width: 100%;
`;
